import { useEffect, useMemo, useRef, useState } from 'react';

import { SSE } from 'sse.js';

import {
  ProceduralStreamBlock,
  isTerminalProceduralBlock,
} from '../../../../backend/src/matter/procedural-timeline/dto/procedural-stream-block.response.dto';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

export type ExtractionPhase = 'reading' | 'extracting' | 'merging' | 'saving' | 'done' | 'failed';

export interface ExtractionState {
  phase: ExtractionPhase;
  chunkCount: number | null;
  eventCount: number | null;
  decisionCount: number | null;
  persisted: { adds: number; updates: number; skipped: number } | null;
  error: string | null;
}

interface UseProceduralStreamProps {
  matterId: string;
  fileId: string | null;
  onTerminal?: (block: ProceduralStreamBlock, state: ExtractionState) => void;
}

const PHASE_PRIORITY: Record<ExtractionPhase, number> = {
  reading: 0,
  extracting: 1,
  merging: 2,
  saving: 3,
  done: 4,
  failed: 4,
};

function deriveState(blocks: ProceduralStreamBlock[]): ExtractionState {
  const state: ExtractionState = {
    phase: 'reading',
    chunkCount: null,
    eventCount: null,
    decisionCount: null,
    persisted: null,
    error: null,
  };

  const advance = (next: ExtractionPhase) => {
    if (PHASE_PRIORITY[next] >= PHASE_PRIORITY[state.phase]) state.phase = next;
  };

  for (const block of blocks) {
    switch (block.type) {
      case 'ocr-started':
      case 'started':
        advance('reading');
        break;
      case 'ocr-completed':
        state.chunkCount = block.chunk_count;
        break;
      case 'extract-started':
        advance('extracting');
        break;
      case 'extract-progress':
        advance('extracting');
        state.eventCount = block.event_count;
        break;
      case 'extract-completed':
        advance('extracting');
        break;
      case 'merge-started':
        advance('merging');
        break;
      case 'merge-progress':
        advance('merging');
        state.decisionCount = block.decision_count;
        break;
      case 'merge-completed':
        advance('merging');
        break;
      case 'persisted':
        advance('saving');
        state.persisted = { adds: block.adds, updates: block.updates, skipped: block.skipped };
        break;
      case 'completed':
        advance('done');
        break;
      case 'failed':
      case 'rejected':
        state.phase = 'failed';
        state.error = block.error;
        break;
      case 'heartbeat':
        break;
    }
  }

  return state;
}

export const useProceduralStream = ({ matterId, fileId, onTerminal }: UseProceduralStreamProps) => {
  const [blocks, setBlocks] = useState<ProceduralStreamBlock[]>([]);
  const blocksRef = useRef<ProceduralStreamBlock[]>([]);
  const sseRef = useRef<SSE | null>(null);
  const seenTimestampsRef = useRef<Set<number>>(new Set());
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const onTerminalRef = useRef(onTerminal);
  const { fetchConfigGET } = useGetFetchConfig();

  // Keep latest onTerminal in a ref so the SSE handler doesn't need to be rebuilt on prop change
  onTerminalRef.current = onTerminal;

  useEffect(() => {
    isMountedRef.current = true;
    setBlocks([]);
    blocksRef.current = [];
    seenTimestampsRef.current = new Set();

    const connect = () => {
      if (!isMountedRef.current || !fileId) return;

      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }

      const url = `${APIBaseChronos}/api/matter/${matterId}/procedural-timeline/files/${fileId}/stream`;

      try {
        const eventSource = new SSE(url, {
          headers: Object.fromEntries(fetchConfigGET.headers.entries()),
        });

        eventSource.onmessage = (event: { data?: string }) => {
          if (!isMountedRef.current) return;
          if (!event.data || !event.data.trim()) return;

          try {
            const block = JSON.parse(event.data) as ProceduralStreamBlock;
            if (block.type === 'heartbeat') return;
            if (seenTimestampsRef.current.has(block.timestamp)) return;
            seenTimestampsRef.current.add(block.timestamp);

            blocksRef.current = [...blocksRef.current, block];
            setBlocks(blocksRef.current);

            if (isTerminalProceduralBlock(block)) {
              onTerminalRef.current?.(block, deriveState(blocksRef.current));
              if (sseRef.current) {
                sseRef.current.close();
                sseRef.current = null;
              }
            }
          } catch (error) {
            console.error('Failed to parse procedural stream block:', error);
          }
        };

        eventSource.onerror = () => {
          if (!isMountedRef.current) return;
          if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = setTimeout(() => {
            if (isMountedRef.current) connect();
          }, 2000);
        };

        sseRef.current = eventSource;
      } catch (error) {
        console.error('Failed to setup procedural stream event source:', error);
      }
    };

    if (matterId && fileId) connect();

    return () => {
      isMountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
    };
  }, [matterId, fileId, fetchConfigGET.headers]);

  const state = useMemo(() => deriveState(blocks), [blocks]);

  return { state };
};
